/**
 * The top of a Vesper page: a kicker, the headline, and one sentence of
 * meaning — what the page says right now, with what needs you picked out.
 *
 *   FLEET · SKYWARD AIRLINES · QUARTER 9          ← kicker (the lead in purple)
 *   Fleet                                         ← title, the page's one h1
 *   17 aircraft in 3 sub-fleets. 2 are idle.      ← meaning, "2 are idle." in gold
 *
 * `aside` takes the right-hand column — a screen's night board — and `actions`
 * sit under the meaning. The words never narrow past their longest word to
 * make room for a board: a headline too long for the room left takes the
 * board onto the next line instead. `reserveAside` keeps a board's height with none in
 * it, for a page among tabs whose others have one, so the headline does not
 * jump between them. `wide` lets the meaning run the header's width, over a
 * screen that is all one picture — the innovation tree — where a sentence
 * broken in two would take a row of the picture. The meaning is a sentence built from an i18next
 * template; its highlights are `Emphasis` elements, which slot into `<Trans>`
 * as components so a translation can move them:
 *
 *   <Trans i18nKey="fleet.meaning" values={…}
 *          components={{ needs: <Emphasis tone="needsYou" /> }} />
 */

import type { CSSProperties, ReactNode } from 'react';

import styles from './page-header.module.css';

interface PageHeaderProps {
  /** The kicker's first item, in the accent: usually the section. */
  kickerLead?: ReactNode;
  /** The kicker's other items, in the caption ink. */
  kicker?: readonly ReactNode[];
  title: ReactNode;
  /** One sentence: what this page says right now. */
  meaning?: ReactNode;
  /** Under the meaning: the page's actions, its one decisive action among them. */
  actions?: ReactNode;
  /** The right-hand column: a night board. */
  aside?: ReactNode;
  /** No board, but keep the height one gives: the headline lands where its sibling tabs' do. */
  reserveAside?: boolean;
  /** The meaning runs the header's width rather than breaking at a column's. */
  wide?: boolean;
  className?: string;
}

export function PageHeader({
  kickerLead,
  kicker = [],
  title,
  meaning,
  actions,
  aside,
  reserveAside = false,
  wide = false,
  className = '',
}: PageHeaderProps) {
  const hasKicker = kickerLead != null || kicker.length > 0;
  return (
    <header
      className={[styles.header, aside ? styles.withAside : '', !aside && reserveAside ? styles.reserveAside : '', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={styles.words}>
        {hasKicker && (
          <p className={styles.kicker}>
            {kickerLead != null && <span className={styles.kickerLead}>{kickerLead}</span>}
            {kicker.map((item, i) => (
              <span key={i}>{item}</span>
            ))}
          </p>
        )}
        <h1 className={styles.title}>{title}</h1>
        {meaning != null && <p className={wide ? `${styles.meaning} ${styles.wide}` : styles.meaning}>{meaning}</p>}
        {actions != null && <div className={styles.actions}>{actions}</div>}
      </div>
      {aside}
    </header>
  );
}

const EMPHASIS = {
  needsYou: styles.needsYou,
  accent: styles.accent,
  plain: styles.plain,
} as const;

type EmphasisProps = { children?: ReactNode } & (
  | { tone: keyof typeof EMPHASIS; hue?: never }
  /** `hue` as `#rrggbb`: a colour known only at runtime, such as a rival airline's. */
  | { tone: 'hue'; hue: string }
);

/**
 * A run inside a meaning sentence, held on one line — a sign never parts from
 * its figure, nor a name from itself — and highlighted by its tone: `plain`
 * holds a figure together in the sentence's own ink. `children` is optional so
 * it can be a `<Trans>` component.
 */
export function Emphasis({ tone, hue, children }: EmphasisProps) {
  if (tone === 'hue') {
    return (
      <span className={styles.hue} style={{ '--hue': hue } as CSSProperties}>
        {children}
      </span>
    );
  }
  return <span className={EMPHASIS[tone]}>{children}</span>;
}
